import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, Pagination, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { CreditNoteService } from '../../services/creditNote.service';
import { CreditNote } from '../../interfaces/sales/creditNote.interface';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

export const CreditNoteList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SALES_INVOICE_MANAGE);
  const [notes, setNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  const load = async (page = 0) => {
    setLoading(true);
    try {
      const r = await CreditNoteService.listByInstitution(page, 10);
      setNotes(r.content);
      setPageInfo({ currentPage: r.number, totalPages: r.totalPages, totalElements: r.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load credit notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.creditNoteList.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Credit Notes</Typography>
        {canManage && (
          <Button data-help="sales.creditNoteList.new" variant="contained" onClick={() => navigate('/sales/credit-notes/new')}>New Credit Note</Button>
        )}
      </Box>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>
          <Paper data-help-section="sales.creditNoteList.table">
            <ScrollableTable>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell data-help="sales.creditNoteList.col.number">Credit Note</TableCell>
                    <TableCell data-help="sales.creditNoteList.col.invoice">Invoice</TableCell>
                    <TableCell data-help="sales.creditNoteList.col.customer">Customer</TableCell>
                    <TableCell data-help="sales.creditNoteList.col.date">Date</TableCell>
                    <TableCell align="right" data-help="sales.creditNoteList.col.total">Total</TableCell>
                    <TableCell align="right" data-help="sales.creditNoteList.col.unapplied">Unapplied</TableCell>
                    <TableCell data-help="sales.creditNoteList.col.status">Status</TableCell>
                    <TableCell data-help="sales.creditNoteList.col.actions" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notes.map(n => (
                    <TableRow key={n.id}>
                      <TableCell>{n.creditNoteNumber}</TableCell>
                      <TableCell>{n.invoiceNumber ?? '—'}</TableCell>
                      <TableCell>{n.customerName ?? '—'}</TableCell>
                      <TableCell>{formatDate(n.noteDate)}</TableCell>
                      <TableCell align="right">{formatMoney(n.totalAmount)}</TableCell>
                      <TableCell align="right">{formatMoney(n.unappliedAmount)}</TableCell>
                      <TableCell>
                        <Chip data-help="sales.creditNoteList.status" size="small" label={humanizeEnum(n.status)} color={n.status === 'POSTED' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View" arrow>
                          <IconButton data-help="sales.creditNoteList.action.view" size="small" color="primary" aria-label="View" onClick={() => navigate(`/sales/credit-notes/${n.id}`)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {notes.length === 0 && (
                    <TableRow><TableCell colSpan={8}>No credit notes yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollableTable>
          </Paper>
          {pageInfo.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={pageInfo.totalPages} page={pageInfo.currentPage + 1} color="primary"
                onChange={(_e, p) => load(p - 1)} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CreditNoteList;
